import React, { useState, useEffect, useRef } from 'react';
import AdminNav from './AdminNav';
import './AdminLayout.css';
import './EmailCampaigns.css';

import { API, authHeadersOnly } from '../services/apiConfig';

const BASE = API;
const ALLOWED_TYPES = [
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.ms-powerpoint',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation',
];

function EmailCampaigns() {
  const [form, setForm] = useState({ subject: '', message: '' });
  const [attachment, setAttachment] = useState(null);
  const [subscriberCount, setSubscriberCount] = useState(null);
  const [status, setStatus] = useState(null);
  const [preview, setPreview] = useState(false);
  const fileInputRef = useRef();

  useEffect(() => {
    fetch(`${BASE}/api/admin/subscribers`, { headers: authHeadersOnly() })
      .then(r => r.ok ? r.json() : [])
      .then(data => setSubscriberCount(data.length))
      .catch(() => setSubscriberCount(0));
  }, []);

  const handleChange = e => setForm(f => ({ ...f, [e.target.name]: e.target.value }));

  const handleFile = e => {
    const file = e.target.files[0];
    if (!file) return;
    if (!ALLOWED_TYPES.includes(file.type)) {
      setStatus('error_filetype');
      e.target.value = '';
      return;
    }
    setAttachment(file);
    setStatus(null);
  };

  const removeAttachment = () => {
    setAttachment(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const send = async () => {
    if (!form.subject.trim() || !form.message.trim()) {
      setStatus('error_validation');
      return;
    }
    setStatus('loading');
    try {
      const fd = new FormData();
      fd.append('subject', form.subject);
      fd.append('message', form.message);
      if (attachment) fd.append('attachment', attachment);

      const res = await fetch(`${BASE}/api/admin/send-email`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token') || ''}` },
        body: fd,
      });
      if (!res.ok) throw new Error();
      setStatus('success');
    } catch {
      setStatus('error');
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    window.location.href = '/login';
  };

  return (
    <>
      <AdminNav onLogout={handleLogout} />
      <div className="admin-page-wrapper">
        <div className="admin-page-container">
          <div className="admin-page-header">
            <h1 className="admin-page-title">📧 Email Campaigns</h1>
            <p className="admin-page-subtitle">Send newsletters and promotional emails to all subscribers</p>
          </div>

          <div className="admin-stats-bar" style={{ marginBottom: '28px' }}>
            <div className="admin-stat-mini">
              <div className="admin-stat-mini-value">{subscriberCount ?? '—'}</div>
              <div className="admin-stat-mini-label">Total Subscribers</div>
            </div>
          </div>

          <div className="ec-layout">
            <div className="ec-form-card">
              <h3 className="ec-section-title">Compose Campaign</h3>

              <label className="ec-label">Subject</label>
              <input
                name="subject"
                value={form.subject}
                onChange={handleChange}
                placeholder="e.g. Our Latest Newsletter is Here!"
                className="ec-input"
              />

              <label className="ec-label">Message</label>
              <textarea
                name="message"
                value={form.message}
                onChange={handleChange}
                placeholder="Write your email message here..."
                className="ec-textarea"
                rows={8}
              />

              <label className="ec-label">Attachment <span className="ec-label-hint">(PDF, DOC, DOCX, PPT, PPTX)</span></label>
              <div className="ec-file-zone" onClick={() => fileInputRef.current.click()}>
                {attachment ? (
                  <div className="ec-file-selected">
                    <span className="ec-file-icon">📎</span>
                    <span className="ec-file-name">{attachment.name}</span>
                    <span className="ec-file-size">({(attachment.size / 1024).toFixed(0)} KB)</span>
                    <button className="ec-file-remove" onClick={e => { e.stopPropagation(); removeAttachment(); }}>✕</button>
                  </div>
                ) : (
                  <div className="ec-file-placeholder">
                    <span style={{ fontSize: '28px' }}>📄</span>
                    <span>Click to attach a file</span>
                    <span className="ec-file-hint">PDF, Word, or PowerPoint</span>
                  </div>
                )}
              </div>
              <input
                ref={fileInputRef}
                type="file"
                accept=".pdf,.doc,.docx,.ppt,.pptx"
                onChange={handleFile}
                style={{ display: 'none' }}
              />

              {status === 'success' && (
                <div className="ec-alert ec-alert-success">✅ Emails queued and sending to all {subscriberCount} subscribers!</div>
              )}
              {status === 'error' && (
                <div className="ec-alert ec-alert-error">❌ Failed to send. Please try again.</div>
              )}
              {status === 'error_validation' && (
                <div className="ec-alert ec-alert-error">⚠️ Subject and message are required.</div>
              )}
              {status === 'error_filetype' && (
                <div className="ec-alert ec-alert-error">⚠️ Only PDF, DOC, DOCX, PPT, PPTX files are allowed.</div>
              )}

              <div className="ec-actions">
                <button className="ec-btn ec-btn-outline" onClick={() => setPreview(p => !p)}>
                  {preview ? 'Hide Preview' : '👁 Preview Email'}
                </button>
                <button
                  className="ec-btn ec-btn-primary"
                  onClick={send}
                  disabled={status === 'loading'}
                >
                  {status === 'loading' ? 'Sending…' : `🚀 Send to All ${subscriberCount ?? ''} Subscribers`}
                </button>
              </div>
            </div>

            {preview && (
              <div className="ec-preview-card">
                <h3 className="ec-section-title">Email Preview</h3>
                <div className="ec-preview-email">
                  <div className="ec-preview-header">
                    <div className="ec-preview-brand">corgif</div>
                    <div className="ec-preview-tagline">Corporate Gifting, Reimagined</div>
                  </div>
                  <div className="ec-preview-body">
                    <h4 className="ec-preview-subject">{form.subject || 'Your subject here'}</h4>
                    <p className="ec-preview-message">{form.message || 'Your message will appear here...'}</p>
                    {attachment && (
                      <div className="ec-preview-attachment">
                        📎 <span>{attachment.name}</span>
                      </div>
                    )}
                    <div style={{ textAlign: 'center', marginTop: '20px' }}>
                      <span className="ec-preview-cta">Shop Now →</span>
                    </div>
                  </div>
                  <div className="ec-preview-footer">
                    © 2026 corgif. All rights reserved.<br />
                    <small>You received this because you subscribed to corgif updates. <u>Unsubscribe</u></small>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
}

export default EmailCampaigns;
